import {
  Button,
  Callout,
  Collapsible,
  Separator,
  Skeleton,
  toast,
} from "@/components/ui";
import { TRACKERS } from "@/constants/Trackers";
import { trackEvent } from "@/lib/tracking/tracking";
import { isActionAvailable } from "@/utils/available-actions";
import { Observation as FhirObservation } from "@curai/js-fhir-tools";
import { useParams, useSearch } from "@tanstack/react-router";
import { ClipboardCheck } from "lucide-react";
import { useState } from "react";
import type { z } from "zod";
import { useCreateGenericObservation } from "../../network/useCreateGenericObservation";
import { useFillNormalExam } from "../../network/useFillNormalExam";
import { useObservations } from "../../network/useObservations";
import {
  ChartingAvailableAction,
  useChartingAvailableActions,
} from "../network/useChartingAvailableActions";
import { ObjectiveFiles } from "./ObjectiveFiles";
import { Observation } from "./Observation";
import { ObservationEditor } from "./ObservationEditor";
import type { editObservationSchema } from "./schemas";

export const Objective = () => {
  const { patientId } = useParams({ from: "/patient/$patientId" });
  const { encounterId } = useSearch({ from: "/patient/$patientId" });
  const [isAdding, setIsAdding] = useState(false);

  const { data, isError, isLoading, isPending } = useObservations({
    patientId,
    encounterId: encounterId as string,
  });
  const createObservation = useCreateGenericObservation({
    patientId,
    encounterId: encounterId as string,
  });

  const fillNormalExam = useFillNormalExam({
    patientId,
    encounterId: encounterId as string,
  });

  const { data: chartingAvailableActions } = useChartingAvailableActions({
    patientId,
    motherEncounterId: encounterId as string,
  });

  const canWriteObjective = isActionAvailable(
    ChartingAvailableAction.WRITE_OBJECTIVE,
    chartingAvailableActions,
  );

  const canReadObjective =
    canWriteObjective ||
    isActionAvailable(
      ChartingAvailableAction.READ_OBJECTIVE,
      chartingAvailableActions,
    );

  if (!canReadObjective) {
    return null;
  }

  const onSubmit = async (values: z.infer<typeof editObservationSchema>) => {
    try {
      await createObservation.mutateAsync({
        patientId,
        encounterId: encounterId as string,
        category: FhirObservation.Category.EXAM,
        code: {
          coding: [
            {
              code: values.code.coding[0].code,
              system: values.code.coding[0].system,
              display: values.code.coding[0].display,
            },
          ],
          text: values.code.text || values.code.coding[0].display,
        },
        method: FhirObservation.Method.values.MANUAL_FREE_TEXT,
        status: FhirObservation.Status.FINAL,
        fhirValue: {
          valueString: values.observationValue,
        },
      });
      setIsAdding(false);
    } catch (error) {
      toast.error("Failed to create observation");
    }
  };

  const handleFillNormalExam = async () => {
    trackEvent(TRACKERS.CHART_NORMAL_FINDINGS);
    try {
      const { matched, failed } = await fillNormalExam.mutateAsync(
        data?.observationsWithPredefinedCodes ?? [],
      );

      if (matched === 0) {
        toast.error(
          "Couldn't fill normal exam — no matching exam systems found",
        );
      } else if (failed.length > 0) {
        toast.warning(`Couldn't save: ${failed.join(", ")}`);
      }
      // Success is silent — no toast when everything saves.
    } catch {
      toast.error("Failed to fill normal exam");
    }
  };

  if (isError) {
    return (
      <Callout
        className="w-full"
        variant="danger"
        title="Something went wrong!"
        description="Failed to load objective."
      />
    );
  }

  return (
    <Collapsible
      title="Objective"
      defaultOpen
      forcedDropdownActions={[
        {
          label: "Chart Normal Findings",
          onClick: handleFillNormalExam,
          disabled: !canWriteObjective || isPending || fillNormalExam.isPending,
          icon: <ClipboardCheck className="mr-2" size={16} />,
        },
      ]}
      dropdownActionsLoading={fillNormalExam.isPending}
    >
      <div className="flex flex-col px-3">
        <ObjectiveFiles />
      </div>
      <div className="flex flex-col">
        {isPending ? (
          <div className="grid gap-2">
            {Array.from({ length: 3 }).map((_, i) => (
              //  biome-ignore lint/suspicious/noArrayIndexKey: unneeded for loading skeleton
              <Skeleton className="h-6" key={i} />
            ))}
          </div>
        ) : (
          <div>
            {data?.observationsWithPredefinedCodes.map(
              ({ observationResource, observationCode }) => (
                <Observation
                  observation={observationResource}
                  observationCode={observationCode}
                  key={observationCode?.text}
                  disabled={!canWriteObjective}
                />
              ),
            )}
            <Separator />
            {data?.nonPredefinedCodeObservations.map((observation) => (
              <Observation
                observation={observation}
                key={observation.id}
                disabled={!canWriteObjective}
              />
            ))}
            <Separator />
          </div>
        )}
      </div>

      <div className="mt-3">
        {isAdding ? (
          <ObservationEditor
            onCancel={() => setIsAdding(false)}
            isLoading={isLoading || createObservation.isPending}
            onSubmit={onSubmit}
          />
        ) : (
          <Button
            size="xs"
            onClick={() => {
              trackEvent(TRACKERS.ADD_OBJECTIVE);
              setIsAdding(true);
            }}
            className="w-fit"
            variant="secondary"
            disabled={!canWriteObjective}
          >
            Add Objective
          </Button>
        )}
      </div>
    </Collapsible>
  );
};
